import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ApplicationApi, MainSegment } from 'api';
import { cacheTime } from 'api/ReactQueryOptions';
import { UnitApi } from 'api/UnitApi';
import { IBaseResponse } from 'api/axios/responses/BaseResponse';

const unitApi = new UnitApi();

export const unitKeys = {
	all: ['unit'] as const,
	connectedUnits: (customerId: number, filters: object) =>
		[...unitKeys.all, 'connected-units', customerId, filters] as const,

	single: (unitId: number | string) => [...unitKeys.all, unitId] as const,
	structure: (unitId: number | string) =>
		[...unitKeys.single(unitId), 'structure'] as const,
	structureV2: (unitId: number) =>
		[...unitKeys.single(unitId), 'structureV2'] as const,
	info: (unitId: number | string) =>
		[...unitKeys.single(unitId), 'info'] as const,
	variants: (unitId: number) =>
		[...unitKeys.single(unitId), 'variants'] as const,
	evcStatus: (unitId: number) =>
		[...unitKeys.single(unitId), 'evc-status'] as const,
	demo: (unitId: number) => [...unitKeys.single(unitId), 'demo'] as const,
	parts: (unitId: number) => [...unitKeys.single(unitId), 'parts'] as const,
	connectedCustomers: (unitId: number) =>
		[...unitKeys.single(unitId), 'connected-customers'] as const,
	productInOperationData: (unitId: number) =>
		[...unitKeys.single(unitId), 'product-in-operation'] as const,
	commissioningData: (unitId: number) =>
		[...unitKeys.single(unitId), 'commissioning'] as const,
	activeServiceAgreementData: (unitId: number) =>
		[...unitKeys.single(unitId), 'active-service-agreement'] as const,
	serviceAgreementData: (unitId: number) =>
		[...unitKeys.single(unitId), 'service-agreements'] as const,
	campaigns: (
		unitId: number,
		includePerformed: boolean,
		includeClosed: boolean,
		queryAsExternal: boolean
	) =>
		[
			...unitKeys.single(unitId),
			'campaigns',
			includePerformed,
			includeClosed,
			queryAsExternal,
		] as const,
	coverages: (unitId: number) =>
		[...unitKeys.single(unitId), 'coverages'] as const,
	telematic: (unitId: number) =>
		[...unitKeys.single(unitId), 'telematic'] as const,
	eventLog: (unitId: number) =>
		[...unitKeys.single(unitId), 'event-log'] as const,
	publications: (unitId: number) =>
		[...unitKeys.single(unitId), 'publications'] as const,
	serviceAgreementFiles: (unitId: number) =>
		[...unitKeys.single(unitId), 'service-agreement-files'] as const,
};

export const unitStructureQuery = (unitId: number | string) => ({
	queryKey: unitKeys.structure(unitId),
	queryFn: async () =>
		(await unitApi.getUnitStructure(unitId.toString())).data,
	...cacheTime(1),
});

export const unitInfoQuery = (
	api: ApplicationApi,
	unitId: number | string
) => ({
	queryKey: unitKeys.info(unitId),
	queryFn: async () => (await api.getUnitInfoById(unitId.toString())).data,
	...cacheTime(1),
});

export const unitOtherPartsQuery = (api: ApplicationApi, unitId: number) => ({
	queryKey: unitKeys.parts(unitId),
	queryFn: async () =>
		(await api.getOtherPartsForUnit(unitId.toString())).data,
	...cacheTime(5),
});

const unitBaseQuery =
	<TQueryKeyReturn, TReturn>(
		queryFn: (id: number) => TQueryKeyReturn,
		apiCall: (
			api: ApplicationApi,
			id: number
		) => Promise<IBaseResponse<TReturn> | AxiosResponse<TReturn>>
	) =>
	(api: ApplicationApi, unitId: number) => ({
		queryKey: queryFn(unitId),
		queryFn: async () => {
			const { data, status } = await apiCall(api, unitId);
			return status === 204 ? null : data;
		},
	});

export const unitEvcStatusQuery = (api: ApplicationApi, unitId: number) => ({
	queryKey: unitKeys.evcStatus(unitId),
	queryFn: async () => (await api.getUnitEvcStatus(unitId.toString())).data,
	...cacheTime(1),
});

export const unitVariantsQuery = unitBaseQuery(unitKeys.variants, (api, id) =>
	api.compareVariants(id.toString())
);

export const unitConnectedEndCustomersQuery = (
	api: ApplicationApi,
	id: number
) => ({
	queryKey: unitKeys.connectedCustomers(id),
	queryFn: async () =>
		(await api.getOwnerAndUnitManagerEcm(id.toString())).data,
});

export const unitProductInOperationDataQuery = unitBaseQuery(
	unitKeys.productInOperationData,
	(api, id) => api.getProductInOperationData(id.toString())
);

export const unitCommissioningDataQuery = unitBaseQuery(
	unitKeys.commissioningData,
	(api, id) => api.commissioningData(id.toString())
);

export const unitActiveServiceAgreementDataQuery = unitBaseQuery(
	unitKeys.activeServiceAgreementData,
	(api, id) => api.getActiveServiceAgreement(id.toString())
);

export const unitServiceAgreementDataQuery = unitBaseQuery(
	unitKeys.serviceAgreementData,
	(api, id) => api.getServiceAgreements(id.toString())
);

export const unitCoveragesQuery = unitBaseQuery(unitKeys.coverages, (api, id) =>
	api.getCoveragesForUnit(id.toString())
);

export const unitEventLogQuery = (api: ApplicationApi, id: number) => ({
	queryKey: unitKeys.eventLog(id),
	queryFn: async () => (await api.getEventLogsForUnit(id.toString())).data,
});

export const unitCampaignsQuery = (
	api: ApplicationApi,
	id: number,
	includePerformed: boolean,
	includeClosed: boolean,
	queryAsExternal: boolean
) => ({
	queryKey: unitKeys.campaigns(
		id,
		includePerformed,
		includeClosed,
		queryAsExternal
	),
	queryFn: async () => {
		const { data, status } = await api.getUnitCampaignsWithoutPolicy(
			id.toString(),
			{
				includePerformed,
				includeClosed,
				queryAsExternal,
			}
		);
		return status === 204 ? null : data;
	},
});

export const unitIsTelematicQuery = (api: ApplicationApi, unitId: number) => ({
	queryKey: unitKeys.telematic(unitId),
	queryFn: async () =>
		(await api.checkIfUnitHasTelematics({ unitId: unitId.toString() }))
			.data,
});

export type QueryConnectedUnitsFilters = {
	mainSegment?: MainSegment | null;
	name?: string | null;
	status?: string | null;
	brand?: string | null;
	model?: string | null;
	hullId?: string | null;
	mmsi?: string | null;
	imo?: string | null;
	callsign?: string | null;
	machineId?: string | null;
};

export const useQueryConnectedUnits = (
	api: ApplicationApi,
	customerId: number | null | undefined,
	queryFilters: QueryConnectedUnitsFilters
) => {
	const { t } = useTranslation(['common']);
	const { enqueueSnackbar } = useSnackbar();

	const query = useQuery({
		queryKey: unitKeys.connectedUnits(customerId ?? 0, queryFilters),
		queryFn: async () => {
			const response = await api.getConnectedUnits({
				consumerId: customerId as number,
				...queryFilters,
			});
			return response.data;
		},
		enabled: customerId != null && customerId !== 0,
	});

	useEffect(() => {
		if (query.error) {
			enqueueSnackbar(t('error-unexpected'), {
				variant: 'error',
			});
		}
	}, [t, enqueueSnackbar, query.error]);

	return query;
};

export const syncUnitIdQuery = (api: ApplicationApi, unitId: number) => ({
	queryKey: unitKeys.single(unitId),
	queryFn: async () => (await api.getSyncUnitId(unitId.toString())).data,
	...cacheTime(60),
});

export const unitPublicationsQuery = (api: ApplicationApi, unitId: number) => ({
	queryKey: unitKeys.publications(unitId),
	queryFn: async () => {
		const { data, status } = await api.getPublications({
			unitId: unitId.toString(),
		});
		return status === 204 ? null : data;
	},
	...cacheTime(60),
});

export const unitServiceAgreementFilesQuery = (
	api: ApplicationApi,
	unitId: number
) => ({
	queryKey: unitKeys.serviceAgreementFiles(unitId),
	queryFn: async () =>
		(await api.getServiceAgreementFiles(unitId)).data ?? [],
	...cacheTime(5),
	retry: 0,
});
